import { Flex } from '@chakra-ui/react';
import { FormattedMessage } from 'react-intl';
import { SoftLink } from '../../../../components/soft-link/SoftLink';
import { useProductAvailabilityMessages } from '../../../../hooks/useProductAvailabilityMessages';
import { Text } from '../../../../ui-components';
import { isBopis, isExtend, isSDD, isShipTo } from '../../../../utils/product-utils';
import { classNames } from '../../../../utils/tailwind/classNames';
import type { ProductItemEditor } from '../../../../bff-api/baskets/model';

/**
 * When multiple scenarios are true at the same time, this is the precedence:
 *
 * SHIP TO
 * 1. Sku offline
 * 2. No inventory
 * 3. Not enough inventory
 *
 * BOPIS/SDD
 * 1. Global bopis/sdd disabled
 * 2. Sku offline
 * 3. Sku not eligible
 * 4. Store not eligible
 * 5. No inventory
 * 6. Not enough inventory
 */

type ProductAvailabilityMessagesProps = {
    productItem: ProductItemEditor;
};

export function ProductAvailabilityMessages({
    productItem,
}: ProductAvailabilityMessagesProps): React.ReactElement | null {
    const {
        availableToSell,
        hasEnoughStock,
        isPartOfMultipleGiftRegistries,
        isBopisNotEligible,
        isSDDDisabled,
        isBopisDisabled,
        isSDDStoreNotAvailable,
        isBopisStoreNotAvailable,
        isSDDStoreOutOfStock,
        isBopisStoreOutOfStock,
        showSDDWithGRItemMessage,
        associatedProductAvailability,
    } = useProductAvailabilityMessages({ productItem });
    const isExtendItem = isExtend(productItem);

    if (isExtendItem && associatedProductAvailability !== 0) {
        return null;
    }

    const isBopisItem = isBopis(productItem);
    const isSDDItem = isSDD(productItem);

    if (isBopisItem && isBopisDisabled) {
        return thisItemIsUnavailableAtThisTime(productItem.productId);
    }

    if (isSDDItem && isSDDDisabled) {
        return notEligibleForSDDTryAnotherDelivery(productItem.productId, productItem.c_pwaUrl);
    }

    if (!productItem.c_online || (isShipTo(productItem) && availableToSell <= 0)) {
        return thisItemIsOutOfStockPleaseRemoveItToContinue(productItem.productId);
    }

    if (isBopisItem && isBopisNotEligible) {
        return thisItemIsUnavailableAtThisTime(productItem.productId);
    }

    if (isBopisItem && isBopisStoreNotAvailable) {
        return thisItemIsUnavailableAtThisLocation(productItem.productId);
    }

    if (isSDDItem && showSDDWithGRItemMessage) {
        return sddGiftRegistryNotEligible(productItem.productId);
    }

    if (isSDDItem && (isBopisNotEligible || isSDDStoreNotAvailable)) {
        return notEligibleForSDDTryAnotherDelivery(productItem.productId, productItem.c_pwaUrl);
    }

    if ((isBopisItem && isBopisStoreOutOfStock) || (isSDDItem && isSDDStoreOutOfStock)) {
        return thisItemIsOutOfStockSelectDifferentLocationDelivery(productItem.productId);
    }

    if (!hasEnoughStock) {
        return pleaseAdjustQuantity(productItem.productId, availableToSell);
    }

    if (isPartOfMultipleGiftRegistries) {
        return (
            <Text
                id={`quantity-message-multiple-gift-registries-${productItem.productId}`}
                size="small"
                color="slt-red.1000"
                align={{ 'lg-sm': 'right' }}
            >
                <FormattedMessage
                    id="quantity.message.multiple.gift.registries"
                    defaultMessage="Item(s) ship to one Gift Recipient at the time."
                />
            </Text>
        );
    }

    return null;
}

const thisItemIsUnavailableAtThisTime = (productId: string): React.ReactElement => (
    <Text
        id={`delivery-method-not-eligible-message-${productId}`}
        size="small"
        color="slt-red.1000"
        align={{ 'lg-sm': 'right' }}
    >
        <FormattedMessage
            id="quantity.message.bopis.disabled"
            defaultMessage="This item is unavailable at this time"
        />
    </Text>
);

const thisItemIsOutOfStockPleaseRemoveItToContinue = (productId: string): React.ReactElement => (
    <div
        data-testid={`quantity-message-out-of-stock-${productId}`}
        className={classNames('flex flex-col', 'lg-sm:items-end lg-sm:text-end')}
    >
        <Text size="small" color="slt-red.1000" align={{ 'lg-sm': 'right' }}>
            <FormattedMessage
                id="quantity.message.out.of.stock"
                defaultMessage="This item is out of stock. Please remove it to continue."
            />
        </Text>
    </div>
);

const thisItemIsUnavailableAtThisLocation = (productId: string): React.ReactElement => (
    <Text
        id={`delivery-method-not-eligible-message-${productId}`}
        size="small"
        color="slt-red.1000"
        align={{ 'lg-sm': 'right' }}
    >
        <FormattedMessage
            id="quantity.message.bopis.store.not-available"
            defaultMessage="This item is unavailable at this location"
        />
    </Text>
);

const thisItemIsOutOfStockSelectDifferentLocationDelivery = (
    productId: string,
): React.ReactElement => (
    <Text
        id={`delivery-method-out-of-stock-message-${productId}`}
        size="small"
        color="slt-red.1000"
        align={{ 'lg-sm': 'right' }}
    >
        <FormattedMessage
            id="quantity.message.out-of-stock"
            defaultMessage="This item is out of stock. Select a different location or delivery option."
        />
    </Text>
);

const pleaseAdjustQuantity = (productId: string, availableToSell: number): React.ReactElement => (
    <Flex
        data-testid="quantity-message-not-available"
        direction="column"
        textAlign={{ base: 'start', 'lg-sm': 'end' }}
    >
        <Text size="small" color="slt-red.1000" align={{ 'lg-sm': 'right' }}>
            <FormattedMessage
                id="quantity.message.items.available"
                defaultMessage="{availableToSell, plural, one {# item} other {# items}} available."
                values={{
                    availableToSell,
                }}
            />
        </Text>
        <Text size="small" color="slt-red.1000">
            <FormattedMessage
                id="quantity.message.adjust"
                defaultMessage="Please adjust quantity."
            />
        </Text>
    </Flex>
);

const notEligibleForSDDTryAnotherDelivery = (
    productId: string,
    pwaUrl = '',
): React.ReactElement => (
    <Text
        id={`delivery-method-not-eligible-message-${productId}`}
        size="small"
        color="slt-red.1000"
        align={{ 'lg-sm': 'right' }}
    >
        <FormattedMessage
            id="quantity.message.sdd.not-available"
            defaultMessage="Your location is not eligible for Same-Day Delivery. Please "
        />{' '}
        <SoftLink to={pwaUrl} className="underline">
            <FormattedMessage
                id="quantity.message.sdd.not-available.link"
                defaultMessage="try another delivery method."
            />
        </SoftLink>
    </Text>
);

const sddGiftRegistryNotEligible = (productId: string): React.ReactElement => (
    <Text
        id={`sdd-gift-registry-not-eligible-message-${productId}`}
        size="small"
        color="slt-red.1000"
        align={{ 'lg-sm': 'right' }}
    >
        <FormattedMessage
            id="product.same_day_delivery.gift_registry_not_eligible"
            defaultMessage="Same-Day Delivery items can not be combined with gift registry items."
        />
    </Text>
);
